import { useMemo } from "react";
import { Button } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useUpdateZoneZonesIdPatchMutation } from "api";
import { SelectInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";

import {
  buildZoneFormSchema,
  ZoneFormSchema,
  zoneFormSchema,
} from "./zoneFormSchema";

type UpdateZoneFormProps = {
  zone: IZone;
  zones: IZone[];
  disabled?: boolean;
};

export function UpdateZoneForm({
  zone,
  zones,
  disabled = false,
}: UpdateZoneFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [updateZone] = useUpdateZoneZonesIdPatchMutation();

  const schema = useMemo(
    () => buildZoneFormSchema(zones, t, zone.id),
    [zones, t, zone.id],
  );

  const { handleSubmit, control } = useForm<ZoneFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: zoneFormSchema.parse(zone),
  });

  const onUpdateZone = async (item: ZoneFormSchema) => {
    try {
      await updateZone({ id: zone.id, zoneUpdate: item });

      navigate(`/administration/zones/${zone.id}/settings`);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onUpdateZone)}>
      <SectionCard title={t("Zone Information")}>
        <TextInput
          label={t("Zone name")}
          control={control}
          required
          disabled={disabled}
          placeholder={t("zone_name_placeholder")}
          fieldName={"name"}
        />
        <TextInput
          label={t("Description (optional)")}
          control={control}
          disabled={disabled}
          placeholder={t("zone_description_placeholder")}
          fieldName={"description"}
        />
        <SelectInput
          label={t("Parent zone")}
          control={control}
          disabled
          placeholder={t("zone_description_placeholder")}
          fieldName={"parent_id"}
          options={zones.map((z) => ({
            ...z,
            label: z.name,
            disabled: !z.hasAccess,
            padding: (z.level - 1) * 2,
          }))}
        />
        <Button type={"submit"}>{t("Save")}</Button>
      </SectionCard>
    </form>
  );
}
